import React from 'react'
import Blob from '@/components/Blob'
import Card from '@/components/ui/Card'
import Illustration from '@/components/Illustration'
import { formatET } from '@/lib/time'
import Editable from '@/components/Editable'

const PROGRAM_OPTIONS = [
  { v: '', label: 'Select a program' },
  { v: 'sponsor-classroom', label: 'Sponsor a Classroom' },
  { v: 'rent-assistance', label: 'Rent Assistance' },
  { v: 'tower-smbs', label: 'Tower for SMBs' },
  { v: 'sponsor-athlete', label: 'Sponsor an Athlete (Prospect)' },
]

export default function Contact() {
  return (
    <div className="space-y-12">
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-shell via-white to-shell px-6 py-12 shadow-soft">
        <Blob className="absolute -top-24 -right-20 h-60 w-60 opacity-40" />
        <Blob className="absolute -bottom-24 -left-16 h-48 w-48 opacity-30" />
        <div className="relative z-10 space-y-5 text-cocoa">
          <span className="inline-flex items-center rounded-full bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cocoa/70">
            Get in touch
          </span>
          <h1 className="text-4xl font-semibold tracking-tight">We’re here for classrooms, sponsors, and community partners.</h1>
          <p className="text-lg leading-relaxed text-cocoa/80">
            Have a question about partnering with Project Tower or supporting a classroom? Drop us a note and our team will reach out within one business day.
          </p>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[2fr,1fr]">
        <Card className="relative overflow-hidden">
          <Blob className="absolute -top-16 -right-12 h-44 w-44 opacity-30" />
          <div className="relative z-10 space-y-6">
            <header className="space-y-2">
              <h2 className="text-2xl font-semibold text-cocoa">Send us a message</h2>
              <p className="text-sm text-cocoa/70">
                Fill out the form and someone from the Project Tower team will reply shortly with the information you need.
              </p>
            </header>
            <form
              name="contact"
              method="POST"
              action="/contact/thanks"
              data-netlify="true"
              netlify-honeypot="bot-field"
              className="space-y-4"
            >
              <input type="hidden" name="form-name" value="contact" />
              <p className="hidden">
                <label className="text-sm text-cocoa/70">
                  Don’t fill this out: <input name="bot-field" />
                </label>
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="space-y-2 text-sm text-cocoa/80">
                  <span className="font-medium text-cocoa">Name</span>
                  <input
                    name="name"
                    required
                    placeholder="Your name"
                    className="w-full rounded-xl border border-cocoa/10 bg-white/90 px-4 py-3 text-base text-cocoa shadow-inner transition focus:border-brick/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick focus-visible:ring-2 focus-visible:ring-brick/20"
                  />
                </label>
                <label className="space-y-2 text-sm text-cocoa/80">
                  <span className="font-medium text-cocoa">Email</span>
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="Email"
                    className="w-full rounded-xl border border-cocoa/10 bg-white/90 px-4 py-3 text-base text-cocoa shadow-inner transition focus:border-brick/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick focus-visible:ring-2 focus-visible:ring-brick/20"
                  />
                </label>
              </div>
              <label className="space-y-2 text-sm text-cocoa/80">
                <span className="font-medium text-cocoa">I'm interested in</span>
                <select
                  name="program_interest"
                  required
                  defaultValue=""
                  className="w-full rounded-xl border border-cocoa/10 bg-white/90 px-4 py-3 text-base text-cocoa shadow-inner transition focus:border-brick/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick focus-visible:ring-2 focus-visible:ring-brick/20"
                >
                  {PROGRAM_OPTIONS.map((o) => (
                    <option key={o.v} value={o.v}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="space-y-2 text-sm text-cocoa/80">
                <span className="font-medium text-cocoa">How can we help?</span>
                <textarea
                  name="message"
                  required
                  rows={5}
                  placeholder="Tell us a little about your classroom or question."
                  className="w-full rounded-xl border border-cocoa/10 bg-white/90 px-4 py-3 text-base text-cocoa shadow-inner transition focus:border-brick/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick focus-visible:ring-2 focus-visible:ring-brick/20"
                />
              </label>
              <button type="submit" className="btn">
                Send message
              </button>
            </form>
          </div>
        </Card>

        <Card className="relative overflow-hidden">
          <Blob className="absolute -bottom-20 -right-12 h-48 w-48 opacity-30" />
          <div className="relative z-10 space-y-6 text-sm text-cocoa/80">
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-1">
                <h2 className="text-lg font-semibold text-cocoa">Visit Project Tower</h2>
                <p>Owings Mills, MD</p>
              </div>
              <Illustration name="leaf" className="h-16 w-16 flex-shrink-0" animate />
            </div>
            <div className="space-y-3">
              <div>
                <span className="block text-xs font-semibold uppercase tracking-wide text-cocoa/60">Email</span>
                <a href="mailto:hello@projecttower.org" className="text-cocoa hover:text-brick transition">
                  hello@projecttower.org
                </a>
              </div>
              <div>
                <span className="block text-xs font-semibold uppercase tracking-wide text-cocoa/60">Phone</span>
                <a href="tel:+16674996633" className="text-cocoa hover:text-brick transition">
                  667.499.6633
                </a>
              </div>
              <div>
                <span className="block text-xs font-semibold uppercase tracking-wide text-cocoa/60">Office hours</span>
                <p>
                  <Editable tkey="contact.hours">Monday–Friday, 9am–5pm (ET)</Editable>
                </p>
                <p className="text-sm text-cocoa/70">
                  Current time (Eastern): <span className="font-semibold">{formatET()}</span>
                </p>
              </div>
            </div>
          </div>
        </Card>
      </section>
    </div>
  )
}
